import { GoogleOAuthProvider } from '@react-oauth/google'
import { observer } from 'mobx-react'
import React, { useEffect, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import { Link, Route, Routes, useLocation } from 'react-router-dom'
import styled, { createGlobalStyle } from 'styled-components'
import { PrimaryButton, SecondaryButton, IconButton } from './components/Button'
import Dropdown, { DropdownItem } from './components/Dropdown'
import { InputLabel, TextInput } from './components/Input'
import InviteAcceptModal from './components/InviteAcceptModal'
import Modal, { ModalFooter } from './components/Modal'
import SearchBox from './components/SearchBox'
import Tooltip from './components/Tooltip'
import WithAuthentication from './components/WithAuthentication'
import { Chevron } from './icons/Chevron'
import { HoverInfoIcon } from './icons/Info'
import AnalyticsIcon from './icons/nav/Analytics'
import PaymentsIcon from './icons/nav/Payments'
import PayoutsNavIcon from './icons/nav/Payouts'
import CustomersIcon from './icons/nav/Customers'
import DevelopersIcon from './icons/nav/Developers'
import FundsIcon from './icons/nav/Funds'
import HomeIcon from './icons/nav/Home'
import ProductsIcon from './icons/nav/Products'
import SettingsIcon from './icons/nav/Settings'
import SupportNavIcon from './icons/nav/Support'
import RadomLogo from './icons/Radom'
import { Menu } from './icons/Menu'
import EventsState from './state/Events'
import NavbarController from './state/NavbarController'
import Radom, { IOrganization } from './state/Radom'
import Treasury from './state/Treasury'
import User from './state/User'
import { GOOGLE_OAUTH_CLIENT_ID, RADOM_COLORS } from './util/Constants'
import { errorToast, successToast, useTestMode, ROUTES } from './util/Util'
import Subscriptions from './views/Billing/Subscriptions'
import Transactions from './views/Transactions'
import CheckoutSession from './views/CheckoutSession'
import CheckoutSessions from './views/CheckoutSessions'
import Customers from './views/Customers'
import CustomerView from './views/Customers/Customer'
import APITokens from './views/Developers/APITokens'
import Developers from './views/Developers/Developers'
import TestnetTokens from './views/Developers/TestnetTokens'
import ViewWebhook from './views/Developers/ViewWebhook'
import Webhooks from './views/Developers/Webhooks'
import DiscountCodes from './views/DiscountCodes'
import Funds from './views/Funds'
import Accounts from './views/Funds/Accounts'
import AutomationView from './views/Funds/Automations'
import AnalyticsView from './views/Analytics'
import Home from './views/Home'
import Invoices from './views/Invoices'
import CreateInvoice from './views/Invoices/CreateInvoice'
import ViewInvoice from './views/Invoices/ViewInvoice'
import NotFound from './views/NotFound'
import Profile from './views/Organization/Overview'
import Payment from './views/Payment'
import PaymentLinks from './views/PaymentLinks'
import { CreatePaymentLink } from './views/PaymentLinks/CreatePaymentLink'
import EditPaymentLink from './views/PaymentLinks/EditPaymentLink'
import Products from './views/Products'
import { CreateDiscount } from './views/Products/CreateDiscountCode'
import CreateProduct from './views/Products/CreateProduct'
import EditProduct from './views/Products/EditProduct'
import Subscription from './views/Subscription'
import CreateAPIRole from './views/Team/CreateAPIRole'
import Verify from './views/Verify'
import Deposit from './views/Funds/Deposit'
import Funding from './views/Funds/Funding'
import { Refund as RefundView } from './views/Refund'
import Withdrawal from './views/Withdrawal'
import SwapView from './views/Swap'
import Settings from './views/Settings'
import Support from './views/Support'
import SupportTicket from './views/SupportTicket'
import ViewPaymentLink from './views/PaymentLinks/ViewPaymentLink'
import DepositAddresses from './views/DepositAddresses'
import Spinner from './components/Spinner'
import Close from './icons/Close'
import Checkmark from './icons/Checkmark'
import Copy from './icons/Copy'
import VirtualAccounts from './views/VirtualAccounts'
import MassivePayouts from './views/MassivePayouts'
import CreateMassivePayout from './views/Funds/CreateMassivePayout'
import MassivePayout from './views/MassivePayout'
import Payouts from './views/Payouts'
import CreatePayout from './views/Funds/CreatePayout'
import Payout from './views/Payout'
import TransactionNavbarIcon from './icons/nav/TransactionNavbarIcon'
import NotificationDropdown from './components/NotificationDropdown/NotifiactionDropdown'
import Docs from './icons/nav/Docs'
import HelpDropdown from './components/HelpDropdown/HelpDropdown'

const GlobalStyles = createGlobalStyle<{ isModalOpen: boolean }>`
  body {
    ${({ isModalOpen }) => isModalOpen && 'overflow: hidden;'}
  }
`

const RadomNavContainer = styled.div<{ $isHidden: boolean, $isMobile: boolean }>`
  position: fixed;
  display: flex;
  flex-direction: column;
  z-index: ${({ $isMobile }) => $isMobile ? 6 : 3};
  transition: 0.3s ease all;
  height: 100vh;
  justify-content: space-between;
  border-right: 1px solid #edf1f7;
  box-sizing: border-box;
  width: 228px;
  background: white;
  transform: translateX(${({ $isHidden }) => $isHidden ? '-400px' : '0'});
  overflow: hidden;

  @media (max-width: 900px) {
    width: 80vw;
    max-width: 260px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`

const RadomNav = styled.div`
  transition: 0.2s ease all;
  background: white;
  display: flex;
  align-items: flex-start;
  gap: 20px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

const NavRadomLogo = styled(Link)`
  cursor: pointer;
  height: 100%;
  display: flex;
  align-items: center;
  text-decoration: none;

  :hover {
    opacity: 0.9;
  }

  :active {
    opacity: 1;
  }
`

const NavItem = styled(Link) <{ $$active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  height: auto;
  font-size: 14px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  padding: 0;
  user-select: none;
  text-decoration: none;
  width: 100%;
  white-space: normal;
  ${({ $$active }) => $$active && `color: ${RADOM_COLORS.BLACK};`}
  ${({ $$active }) => $$active && `
    svg {
      stroke: ${RADOM_COLORS.BLACK};
    }
  `}

  svg {
    transition: 0.2s ease all;
  }

  :hover {
    opacity: 1;

    svg {
      stroke: ${RADOM_COLORS.BLACK};
    }
  }

  :active {
    opacity: 0.5;
  }
`

const NavItemGroup = styled.div<{ $$active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  height: auto;
  font-size: 14px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  padding: 0;
  user-select: none;
  text-decoration: none;
  width: 100%;
  white-space: normal;

  svg {
    transition: 0.2s ease all;
  }

  ${({ $$active }) => $$active && `
    svg {
      stroke: ${RADOM_COLORS.BLACK};
    }
  `}

  :hover {
    opacity: 1;

    svg {
      stroke: ${RADOM_COLORS.BLACK};
    }
  }

  :active {
    ${({ $$active }) => !$$active && 'opacity: 0.5;'}
  }
`

const NavItemGroupContainer = styled.div<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  transform-origin: 0 0;
  transition:
    max-height 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.2s ease,
    transform 0.24s ease,
    padding-bottom 0.24s ease;
  overflow: hidden;
  gap: 6px;
  padding-top: ${({ $active }) => $active ? '9px' : '0'};
  padding-bottom: ${({ $active }) => $active ? '9px' : '0'};
  max-height: ${({ $active }) => $active ? '760px' : '0'};
  opacity: ${({ $active }) => $active ? 1 : 0};
  transform: translateY(${({ $active }) => $active ? '0' : '-4px'});
  pointer-events: ${({ $active }) => $active ? 'auto' : 'none'};
  will-change: max-height, opacity, transform;
`

const NavItemText = styled.div<{ $active: boolean }>`
  padding: 7px 11px;
  transition: 0.2s ease all;
  border-radius: 10px;
  box-sizing: border-box;
  border: 1px solid transparent;
  background: transparent;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #566071;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.34;
  min-height: 33px;
  white-space: normal;

  ${({ $active }) => $active && `
    background: #f3f4f6;
    color: ${RADOM_COLORS.BLACK};
    border-color: #eceff3;
  `}

  &:hover {
    ${({ $active }) => !$active && 'background: #f7f7f8;'}
  }
`

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 5;
  transition: opacity 0.2s ease;

  @media (min-width: 901px) {
    display: none;
  }
`

const NavItemGroupText = styled.div<{ $active: boolean }>`
  padding: 7px 11px;
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.12s ease;
  border-radius: 10px;
  box-sizing: border-box;
  background: transparent;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  border: 1px solid transparent;
  color: #566071;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.34;
  min-height: 33px;
  cursor: pointer;
  user-select: none;

  ${({ $active }) => $active && `
    background: #f3f4f6;
    color: ${RADOM_COLORS.BLACK};
    border-color: #eceff3;
  `}

  &:hover {
    ${({ $active }) => !$active && 'background: #f7f7f8;'}

    #chevron {
      opacity: 0.88;
    }
  }

  &:active {
    transform: translateY(1px);
  }

  #chevron {
    opacity: ${({ $active }) => $active ? 0.82 : 0.42};
    transform: rotate(${({ $active }) => $active ? -180 : 0}deg);
    transition: transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.18s ease;
  }
`

const SelectedTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid #e7eaf0;
  color: ${RADOM_COLORS.BLACK};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
`

const OrganizationSectionLabel = styled.div`
  color: #8a91a0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  padding: 6px 0 4px;
  text-transform: uppercase;
`

const OrganizationSectionDivider = styled.div`
  border-top: 1px solid #eef1f5;
  margin: 8px 12px 0;
`

const OrganizationModalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

const OrganizationRowShell = styled.div`
  width: 100%;
`

const OrganizationRowButton = styled.div<{
  $selected?: boolean
  $nested?: boolean
  $disabled?: boolean
  $context: 'dropdown' | 'modal'
}>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  min-height: ${({ $context, $nested }) => {
    if ($context === 'modal') {
      return $nested ? '54px' : '60px'
    }

    return $nested ? '46px' : '52px'
  }};
  padding: ${({ $context, $nested }) => {
    if ($context === 'modal') {
      return $nested ? '10px 12px' : '11px 12px'
    }

    return $nested ? '8px 12px' : '9px 12px'
  }};
  background: ${({ $selected }) => $selected ? '#f6f7f9' : 'transparent'};
  border: 1px solid ${({ $selected }) => $selected ? '#e7eaf0' : 'transparent'};
  border-radius: 12px;
  gap: 12px;
  cursor: ${({ $disabled }) => $disabled ? 'default' : 'pointer'};
  text-align: left;
  transition: 0.16s ease background-color, 0.16s ease color, 0.16s ease border-color;

  &:hover {
    background: ${({ $selected, $disabled }) => $disabled
    ? ($selected ? '#f6f7f9' : 'transparent')
    : ($selected ? '#f1f3f6' : '#f7f8fa')};
    border-color: ${({ $selected, $disabled }) => $disabled
    ? ($selected ? '#e7eaf0' : 'transparent')
    : '#e7eaf0'};
  }

  opacity: ${({ $disabled }) => $disabled ? 0.65 : 1};
`

const OrganizationRowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`

const OrganizationRowTitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex-wrap: wrap;
`

const OrganizationRowTitle = styled.span<{ $context: 'dropdown' | 'modal', $allowWrap?: boolean }>`
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: ${({ $context }) => $context === 'modal' ? '14.5px' : '13.25px'};
  font-weight: ${({ $context }) => $context === 'modal' ? 520 : 470};
  line-height: 1.35;
  flex: 1;
  overflow: ${({ $allowWrap }) => $allowWrap ? 'visible' : 'hidden'};
  text-overflow: ${({ $allowWrap }) => $allowWrap ? 'clip' : 'ellipsis'};
  white-space: ${({ $allowWrap }) => $allowWrap ? 'normal' : 'nowrap'};
`

const OrganizationRelationLabel = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid #e7eaf0;
  background: #f7f8fa;
  color: #7d8594;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`

const OrganizationRowMeta = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`

const OrganizationRowId = styled.span<{ $context: 'dropdown' | 'modal' }>`
  color: ${RADOM_COLORS.GRAY_DARKER};
  font-family: var(--font-mono);
  font-size: ${({ $context }) => $context === 'modal' ? '11.85px' : '11.35px'};
  line-height: 1.45;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const OrganizationInlineCopyButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #8a91a0;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 22px;
  justify-content: center;
  padding: 0;
  transition: 0.16s ease background-color, 0.16s ease color;
  width: 22px;

  &:hover {
    background: #eef1f5;
    color: ${RADOM_COLORS.BLACK};
  }
`

const OrganizationRowHint = styled.span`
  color: #8a91a0;
  font-size: 11.75px;
  line-height: 1.45;
`

const OrganizationRowTrailing = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-top: 2px;
`

const OrganizationTick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  color: ${RADOM_COLORS.BLACK};
`

const OrganizationChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-left: 24px;
  padding-left: 18px;
  border-left: 1px solid #dde4ee;
`

const ModePill = styled.span<{ $test?: boolean }>`
  align-items: center;
  background: ${({ $test }) => $test ? '#f3f4f6' : 'transparent'};
  border: 1px solid #e7eaf0;
  border-radius: 999px;
  color: #5f6675;
  display: inline-flex;
  justify-content: center;
  padding: 3px 7px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
`

const ModeToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 999px;
  border: 1px solid #e7eaf0;
  background: #f7f8fa;
`

const ModeToggleButton = styled.button<{ $active?: boolean }>`
  min-width: 58px;
  height: 28px;
  padding: 0 12px;
  border: 0;
  border-radius: 999px;
  background: ${({ $active }) => $active ? '#ffffff' : 'transparent'};
  box-shadow: ${({ $active }) => $active ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? RADOM_COLORS.BLACK : '#7b8392'};
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: 0.16s ease background-color, 0.16s ease color, 0.16s ease box-shadow;

  &:hover {
    color: ${RADOM_COLORS.BLACK};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: ${({ $active }) => $active ? 1 : 0.55};
  }
`

const OrganizationTreeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const OrganizationDropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 13px;
  width: 100%;
  min-height: 0;
  max-height: inherit;
`

const OrganizationDropdownBody = styled.div`
  width: 100%;
  padding: 10px 0 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`

const OrganizationDropdownActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 8px 0;
`

const OrganizationDropdownActionLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
`

const OrganizationDropdownActionHint = styled.span`
  color: #8a91a0;
  font-size: 11.5px;
  line-height: 1.45;
`

const OrganizationDropdownFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 8px 8px;
  margin-top: 0;
  background: #ffffff;
  border-top: 1px solid #eef1f5;
  flex: 0 0 auto;
`

const AppBlocker = styled.div`
  position: fixed;
  inset: 0;
  z-index: 130;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`

const AppBlockerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: min(360px, calc(100vw - 32px));
  max-width: min(420px, calc(100vw - 32px));
  padding: 18px 20px;
  border-radius: 20px;
  border: 1px solid #eceff3;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.14);
`

const RadomBottomNav = styled.div`
  flex-shrink: 0;
  padding: 8px 14px 26px;
  border-top: 1px solid #edf1f7;
`

interface RouteConfig {
  path?: string
  name?: string
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
  element?: React.ReactNode
  children?: RouteConfig[]
  requireAPI?: boolean
  features?: string[]
  isBottomNav?: boolean
  isExternal?: boolean
}
const routes: RouteConfig[] = [
  {
    path: '/',
    name: 'Home',
    icon: HomeIcon,
    element: <Home />
  },
  {
    name: 'Funds',
    icon: FundsIcon,
    children: [
      {
        path: '/funds',
        name: 'Balances',
        element: <Funds />
      },
      {
        path: '/funding',
        name: 'Funding',
        element: <Funding />
      },
      {
        path: '/accounts',
        name: 'Withdrawal accounts',
        element: <Accounts />
      },

      {
        path: '/virtual_accounts',
        name: 'Virtual accounts',
        element: <VirtualAccounts />
      },
      {
        path: '/automation',
        name: 'Automations',
        element: <AutomationView />
      }
    ]
  },
  {
    path: '/transactions',
    name: 'Transactions',
    icon: TransactionNavbarIcon,
    element: <Transactions />
  },
  {
    name: 'Payin',
    path: '/payment_links',
    element: <PaymentLinks />,
    icon: PaymentsIcon,
    children: [
      {
        path: '/deposit_addresses',
        name: 'Deposit accounts',
        element: <DepositAddresses />
      },
      {
        path: '/payment_links',
        name: 'Payment links',
        element: <PaymentLinks />,
        requireAPI: true
      },
      {
        path: '/invoices',
        element: <Invoices />,
        name: 'Invoices',
        requireAPI: true
      },
      {
        path: '/checkout_sessions',
        name: 'Checkout sessions',
        element: <CheckoutSessions />,
        requireAPI: true
      },
      {
        path: '/subscriptions',
        name: 'Subscriptions',
        element: <Subscriptions />
      }
    ]
  },
  {
    name: 'Payout',
    path: ROUTES.MASSIVE_PAYOUTS,
    element: <MassivePayouts />,
    icon: PayoutsNavIcon,
    children: [
      {
        path: ROUTES.MASSIVE_PAYOUTS,
        name: 'Mass payouts',
        element: <MassivePayouts />
      },
      {
        path: ROUTES.PAYOUTS,
        name: 'Payouts',
        element: <Payouts />
      }
    ]
  },

  {
    path: '/analytics',
    name: 'Analytics',
    icon: AnalyticsIcon,
    element: <AnalyticsView />
  },
  {
    name: 'Catalog',
    path: '/products',
    element: <Products />,
    icon: ProductsIcon,
    children: [
      {
        path: '/products',
        name: 'Products',
        element: <Products />,
        requireAPI: true
      },
      {
        path: '/discount_codes',
        name: 'Discount codes',
        element: <DiscountCodes />,
        requireAPI: true
      }
    ]
  },
  {
    path: '/virtual_accounts',
    element: <VirtualAccounts />
  },
  {
    path: '/customers',
    name: 'Customers',
    icon: CustomersIcon,
    element: <Customers />,
    requireAPI: true
  },
  {
    name: 'Developers',
    icon: DevelopersIcon,
    requireAPI: true,
    children: [
      {
        path: '/developers/webhooks',
        name: 'Webhooks',
        element: <Webhooks />
      },
      {
        path: '/developers/api_tokens',
        name: 'API tokens',
        element: <Developers />
      },
      {
        path: '/developers/testnet_tokens',
        name: 'Testnet tokens',
        element: <TestnetTokens />
      }
    ]
  },
  {
    path: '/support',
    name: 'Support',
    icon: SupportNavIcon,
    element: <Support />,
    isBottomNav: true
  },
  {
    path: '/support/:id',
    element: <SupportTicket />
  },
  {
    path: '/settings',
    name: 'Settings',
    icon: SettingsIcon,
    element: <Settings />,
    isBottomNav: true
  },
  {
    path: 'https://docs.radom.com/',
    name: 'Docs',
    icon: Docs,
    element: <></>,
    isBottomNav: true,
    isExternal: true
  },
  {
    path: '/deposit_addresses',
    element: <DepositAddresses />
  },
  {
    path: '/funding',
    element: <Funding />
  },
  {
    path: '/accounts',
    element: <Accounts />
  },
  {
    path: '/invoices',
    element: <Invoices />,
    requireAPI: true
  },
  {
    path: '/checkout_sessions',
    element: <CheckoutSessions />,
    requireAPI: true
  },
  {
    path: '/payment_links',
    element: <PaymentLinks />,
    requireAPI: true
  },
  {
    path: '/funds',
    element: <Funds />
  },
  {
    path: '/subscriptions',
    element: <Subscriptions />
  },
  {
    path: '/products',
    element: <Products />,
    requireAPI: true
  },
  {
    path: '/discount_codes',
    element: <DiscountCodes />,
    requireAPI: true
  },
  {
    path: '/deposit/:id',
    element: <Deposit />
  },
  {
    path: '/refund/:id',
    element: <RefundView />
  },
  {
    path: ROUTES.MASSIVE_PAYOUTS,
    element: <MassivePayouts />
  },
  {
    path: ROUTES.CREATE_MASSIVE_PAYOUT,
    element: <CreateMassivePayout />
  },
  {
    path: ROUTES.PAYOUTS,
    element: <Payouts />
  },

  {
    path: ROUTES.CREATE_PAYOUT,
    element: <CreatePayout />
  },
  {
    path: '/automation',
    element: <AutomationView />
  },
  {
    path: '/invoices/create',
    element: <CreateInvoice />,
    requireAPI: true
  },
  {
    path: '/products/create',
    element: <CreateProduct />,
    requireAPI: true
  },
  {
    path: '/products/:productId',
    element: <EditProduct />,
    requireAPI: true
  },
  {
    path: '/discounts/create',
    element: <CreateDiscount />,
    requireAPI: true
  },
  {
    path: '/payment_links/create',
    element: <CreatePaymentLink />,
    requireAPI: true
  },
  {
    path: '/payment_links/:paymentLinkId',
    element: <EditPaymentLink />,
    requireAPI: true
  },
  {
    path: '/payment_link/detail/:paymentLinkId',
    element: <ViewPaymentLink />,
    requireAPI: true
  },
  {
    path: '/customer/:id',
    element: <CustomerView />,
    requireAPI: true
  },
  {
    path: '/invoices/:invoiceId',
    element: <ViewInvoice />,
    requireAPI: true
  },
  {
    path: '/role/create',
    element: <CreateAPIRole />
  },
  {
    path: '/developers/webhook/:webhookId',
    element: <ViewWebhook />,
    requireAPI: true
  },
  {
    path: '/profile',
    element: <Profile />,
    requireAPI: true
  },
  {
    path: '/payment/:id',
    element: <Payment />
  },
  {
    path: '/withdrawal/:id',
    element: <Withdrawal />
  },
  {
    path: '/swap/:id',
    element: <SwapView />
  },
  {
    path: ROUTES.MASSIVE_PAYOUT_DETAIL,
    element: <MassivePayout />
  },
  {
    path: ROUTES.PAYOUT_DETAIL,
    element: <Payout />
  },
  {
    path: '/checkout_session/:id',
    element: <CheckoutSession />
  },
  {
    path: '/developers/webhooks',
    element: <Webhooks />
  },
  {
    path: '/developers/api_tokens',
    element: <APITokens />
  },
  {
    path: '/developers/testnet_tokens',
    element: <TestnetTokens />
  },
  {
    path: '/subscription/:subscriptionId',
    element: <Subscription />,
    requireAPI: true
  },
  {
    path: '/verify',
    element: <Verify />
  }
]

const IMPERSONATION_ALLOWED_ORG_IDS = new Set([
  '00000000-0000-0000-0000-000000000000',
  '00000000-0000-0000-0000-000000000999'
])
const IMPERSONATION_ORG_ID_KEY = 'IMPERSONATION_ORG_ID'
const UUID_LIKE_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const App = observer(() => {
  const location = useLocation()

  const [isChangingOrganization, setIsChangingOrganization] = useState<boolean>(false)
  const [numNotifications, setNumNotifications] = useState(0)
  const [notificationsCloseFn, setNotificationsCloseFn] = useState(() => () => { })
  const [orgDropdownCloseFn, setOrgDropdownCloseFn] = useState(() => () => { })

  const [isLoadingAckedNotifications, setIsLoadingAckedNotifications] = useState(true)
  const [latestBlockAcks, setLatestBlockAcks] = useState<{ [chainID: number]: number }>({})
  const [latestManagedAck, setLatestManagedAck] = useState(new Date(0))

  const [organizationModalVisible, setOrganizationModalVisible] = useState(false)
  const [createOrganizationModalVisible, setCreateOrganizationModalVisible] = useState(false)
  const [organizationTransitionLabel, setOrganizationTransitionLabel] = useState('')
  const [organizations, setOrganizations] = useState([] as IOrganization[])
  const isUuidLike = (value?: string): boolean => !!value && UUID_LIKE_PATTERN.test(value.trim())
  const organizationDisplayName = (organization?: Pick<IOrganization, 'name' | 'organizationId'>): string => {
    const name = organization?.name?.trim()
    if (name && !isUuidLike(name)) return name
    const shortId = organization?.organizationId?.slice(0, 8)
    return shortId ? `New organization ${shortId}` : 'New organization'
  }
  const organizationIdPreview = (organization?: Pick<IOrganization, 'organizationId'>): string =>
    organization?.organizationId?.slice(0, 8) ?? 'Unavailable'
  const sortOrganizations = (organizationsToSort: IOrganization[]): IOrganization[] => [...organizationsToSort]
    .filter(o => !!o.organizationId)
    .sort((a, b) => {
      if (a.organizationId === User.selectedOrganizationId) return -1
      if (b.organizationId === User.selectedOrganizationId) return 1
      if (a.verificationStatus === 'Verified' && b.verificationStatus !== 'Verified') return -1
      if (b.verificationStatus === 'Verified' && a.verificationStatus !== 'Verified') return 1
      return organizationDisplayName(a).localeCompare(organizationDisplayName(b))
    })
  const uniqueOrganizations = Array.from(new Map(organizations.map(o => [o.organizationId, o])).values())
  const selectedOrg = uniqueOrganizations.find(o =>
    o.organizationId === User.selectedOrganizationId
  ) ?? User.organization
  const isQaPreview = new URLSearchParams(window.location.search).get('qa') === '1'
  const sortedOrganizations = sortOrganizations(uniqueOrganizations)
  const qaPreviewOrganizations = isQaPreview && selectedOrg?.name
    ? [
      selectedOrg,
      {
        organizationId: 'qa-preview-org-secondary',
        name: 'Radom Pay SP z. o.o.',
        verificationStatus: 'Verified'
      },
      {
        organizationId: 'qa-preview-org-tertiary',
        name: 'Radom Treasury Europe',
        verificationStatus: 'Verified'
      },
      {
        organizationId: 'qa-preview-org-fourth',
        name: 'Northstar Holdings Ltd',
        verificationStatus: 'Verified'
      },
      {
        organizationId: 'qa-preview-org-fifth',
        name: 'Keedsler Motors Europe',
        verificationStatus: 'Verified'
      },
      {
        organizationId: 'qa-preview-org-sixth',
        name: 'Radom Pay Limited',
        verificationStatus: 'Verified'
      }
    ] as IOrganization[]
    : []
  const isQaDropdownPreview = qaPreviewOrganizations.length > 0
  const organizationDropdownOrganizations = isQaDropdownPreview
    ? qaPreviewOrganizations
    : sortedOrganizations
  const organizationDropdownOrder = new Map(organizationDropdownOrganizations.map((organization, index) => [
    organization.organizationId,
    index
  ]))
  const currentVisibleOrganization = organizationDropdownOrganizations.find(o =>
    o.organizationId === User.selectedOrganizationId
  ) ?? selectedOrg
  const currentModalOrganization = currentVisibleOrganization
  const organizationsById = new Map(organizationDropdownOrganizations.map(organization => [
    organization.organizationId,
    organization
  ]))
  const childOrganizationsByParent = organizationDropdownOrganizations.reduce<Record<string, IOrganization[]>>(
    (acc, organization) => {
      if (!organization.parentOrganizationId) {
        return acc
      }

      if (!acc[organization.parentOrganizationId]) {
        acc[organization.parentOrganizationId] = []
      }

      acc[organization.parentOrganizationId].push(organization)
      return acc
    },
    {}
  )
  const orderOrganizationsForDisplay = (
    organizationsToOrder: IOrganization[]
  ): IOrganization[] => [...organizationsToOrder]
    .sort((a, b) => {
      const aIndex = organizationDropdownOrder.get(a.organizationId) ?? Number.MAX_SAFE_INTEGER
      const bIndex = organizationDropdownOrder.get(b.organizationId) ?? Number.MAX_SAFE_INTEGER
      return aIndex - bIndex
    })
  const getOrganizationParent = (organization?: IOrganization): IOrganization | undefined =>
    organization?.parentOrganizationId ? organizationsById.get(organization.parentOrganizationId) : undefined
  const getOrganizationChildCount = (organization?: IOrganization): number => {
    if (!organization?.organizationId) {
      return 0
    }

    return Math.max(
      childOrganizationsByParent[organization.organizationId]?.length ?? 0,
      organization.childOrganizationCount ?? 0
    )
  }
  const getOrganizationRelationLabel = (organization?: IOrganization): string | undefined => {
    if (!organization) {
      return undefined
    }

    if (organization.parentOrganizationId) {
      return 'Child organization'
    }

    if (getOrganizationChildCount(organization) > 0) {
      return 'Parent organization'
    }

    return undefined
  }
  const getOrganizationRelationHint = (organization?: IOrganization): string | undefined => {
    if (!organization) {
      return undefined
    }

    if (organization.parentOrganizationId) {
      const parentOrganization = getOrganizationParent(organization)
      const parentLabel = organizationDisplayName(parentOrganization ?? {
        name: organization.parentOrganizationName,
        organizationId: organization.parentOrganizationId
      })
      return `Child of ${parentLabel}`
    }

    const childCount = getOrganizationChildCount(organization)
    if (childCount > 0) {
      return `${childCount} child organization${childCount === 1 ? '' : 's'}`
    }

    return undefined
  }
  const organizationHierarchyRoots = orderOrganizationsForDisplay(
    organizationDropdownOrganizations.filter((organization) => {
      if (!organization.parentOrganizationId) {
        return true
      }

      return !organizationsById.has(organization.parentOrganizationId)
    })
  )
  const collectOrganizationDescendants = (organizationId?: string, visited = new Set<string>()): IOrganization[] => {
    if (!organizationId) {
      return []
    }

    const children = orderOrganizationsForDisplay(childOrganizationsByParent[organizationId] ?? [])
    const descendants: IOrganization[] = []

    for (const child of children) {
      if (!child.organizationId || visited.has(child.organizationId)) {
        continue
      }

      visited.add(child.organizationId)
      descendants.push(child)
      descendants.push(...collectOrganizationDescendants(child.organizationId, visited))
    }

    return descendants
  }
  const relatedOrganizationsMap = new Map<string, IOrganization>()
  const addRelatedOrganization = (organization?: IOrganization): void => {
    if (!organization?.organizationId || organization.organizationId === User.selectedOrganizationId) {
      return
    }

    if (!relatedOrganizationsMap.has(organization.organizationId)) {
      relatedOrganizationsMap.set(organization.organizationId, organization)
    }
  }

  addRelatedOrganization(getOrganizationParent(currentVisibleOrganization))
  if (currentVisibleOrganization?.parentOrganizationId) {
    orderOrganizationsForDisplay(childOrganizationsByParent[currentVisibleOrganization.parentOrganizationId] ?? [])
      .forEach(addRelatedOrganization)
  }
  collectOrganizationDescendants(currentVisibleOrganization?.organizationId).forEach(addRelatedOrganization)

  const fallbackOrganizations = organizationDropdownOrganizations.filter(organization =>
    organization.organizationId !== User.selectedOrganizationId &&
    !relatedOrganizationsMap.has(organization.organizationId)
  )
  const otherVisibleOrganizations = [
    ...Array.from(relatedOrganizationsMap.values()),
    ...fallbackOrganizations
  ].slice(0, isQaDropdownPreview ? 2 : 4)
  const hiddenOrganizationsCount = Math.max(
    0,
    organizationDropdownOrganizations.length - (currentVisibleOrganization ? 1 : 0) - otherVisibleOrganizations.length
  )

  const [testMode, setTestMode] = useTestMode()
  const canUseLiveMode = User.organization?.verificationStatus === 'Verified'
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false)

  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false)
  const [impersonationOrgId, setImpersonationOrgId] = useState('')
  const isImpersonatingOrganization = !!localStorage.getItem(IMPERSONATION_ORG_ID_KEY)
  const isActiveImpersonationSourceOrg = !!User.selectedOrganizationId &&
    IMPERSONATION_ALLOWED_ORG_IDS.has(User.selectedOrganizationId)
  const canShowImpersonationControls = isActiveImpersonationSourceOrg || isImpersonatingOrganization
  const handleImpersonationAction = (): void => {
    orgDropdownCloseFn()

    if (isImpersonatingOrganization) {
      localStorage.removeItem(IMPERSONATION_ORG_ID_KEY)
      window.location = '/' as any
      return
    }

    if (!isActiveImpersonationSourceOrg) {
      return
    }

    window.setTimeout(() => setImpersonationModalOpen(true), 0)
  }

  const [activeNavGroup, setActiveNavGroup] = useState<string>()

  const loadOrganizations = async (): Promise<void> => {
    try {
      const orgIds = Array.from(new Set([
        ...(await Radom.listOrganizations()),
        User.selectedOrganizationId
      ].filter((orgId): orgId is string => !!orgId)))

      const orgs = await Promise.all(orgIds.sort()
        .map(async o => await Radom.getOrganizationById(o)))

      setOrganizations(orgs)
    } catch (err) {
      console.error('Failed to load organizations', err)
    }
  }

  const clearOrganizationTransitionState = (): void => {
    setIsChangingOrganization(false)
    setOrganizationTransitionLabel('')
  }

  const switchOrganization = async (organization: IOrganization): Promise<void> => {
    if (
      !organization.organizationId ||
      isChangingOrganization ||
      User.selectedOrganizationId === organization.organizationId ||
      organization.organizationId === 'qa-preview-org-secondary'
    ) {
      return
    }

    setOrganizationTransitionLabel(organizationDisplayName(organization))
    setOrganizationModalVisible(false)
    orgDropdownCloseFn()
    setIsChangingOrganization(true)

    try {
      await Radom.updateUser({ defaultOrganizationId: organization.organizationId })
    } catch (err: any) {
      clearOrganizationTransitionState()
      errorToast(err.response?.data?.error || err.message)
    }
  }

  const createOrganization = async (): Promise<void> => {
    if (isChangingOrganization) {
      return
    }

    setCreateOrganizationModalVisible(false)
    setOrganizationTransitionLabel('your new organization')
    orgDropdownCloseFn()
    setIsChangingOrganization(true)

    try {
      const { organizationId } = await Radom.createOrganization()
      await Radom.updateUser({ defaultOrganizationId: organizationId })
    } catch (err: any) {
      clearOrganizationTransitionState()
      errorToast(err.response?.data?.error || err.message)
    }
  }

  const copyOrganizationId = async (organization?: Pick<IOrganization, 'organizationId' | 'name'>): Promise<void> => {
    if (!organization?.organizationId) {
      return
    }

    try {
      await navigator.clipboard.writeText(organization.organizationId)
      successToast('Organization ID copied')
    } catch (err) {
      console.error('Failed to copy organization ID', err)
      errorToast('Failed to copy organization ID')
    }
  }

  const renderOrganizationRow = (
    organization: IOrganization,
    options: {
      context: 'dropdown' | 'modal'
      selected?: boolean
      nested?: boolean
      showId?: boolean
      allowCopy?: boolean
      disabled?: boolean
      onClick?: () => void
    }
  ): React.ReactElement => {
    const relationLabel = getOrganizationRelationLabel(organization)
    const relationHint = getOrganizationRelationHint(organization)
    const showId = options.context === 'modal' || options.showId
    const idLabel = showId
      ? organization.organizationId
      : organizationIdPreview(organization)
    const allowWrappedTitle = options.context === 'modal' || options.showId

    return <OrganizationRowShell>
      <OrganizationRowButton
        role={options.onClick ? 'button' : undefined}
        tabIndex={options.disabled || !options.onClick ? -1 : 0}
        aria-disabled={options.onClick ? options.disabled : undefined}
        onClick={options.disabled ? undefined : options.onClick}
        onKeyDown={options.disabled || !options.onClick
          ? undefined
          : (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              options.onClick?.()
            }
          }}
        $context={options.context}
        $nested={options.nested}
        $disabled={options.disabled}
        $selected={options.selected}>
        <OrganizationRowText>
          <OrganizationRowTitleLine>
            <OrganizationRowTitle $context={options.context} $allowWrap={allowWrappedTitle}>
              {organizationDisplayName(organization)}
            </OrganizationRowTitle>
            {options.context === 'modal' && relationLabel && (
              <OrganizationRelationLabel>{relationLabel}</OrganizationRelationLabel>
            )}
          </OrganizationRowTitleLine>
          {showId && (
            <OrganizationRowMeta>
              <OrganizationRowId $context={options.context}>{idLabel}</OrganizationRowId>
              {options.allowCopy && organization.organizationId && (
                <OrganizationInlineCopyButton
                  type='button'
                  aria-label={`Copy organization ID for ${organizationDisplayName(organization)}`}
                  onClick={async (e) => {
                    e.stopPropagation()
                    await copyOrganizationId(organization)
                  }}>
                  <Copy width={14} height={14} style={{ stroke: 'currentColor' }} />
                </OrganizationInlineCopyButton>
              )}
            </OrganizationRowMeta>
          )}
          {options.context === 'modal' && relationHint && (
            <OrganizationRowHint>{relationHint}</OrganizationRowHint>
          )}
        </OrganizationRowText>
        <OrganizationRowTrailing>
          {options.selected
            ? options.context === 'modal'
              ? <SelectedTag>Selected</SelectedTag>
              : <OrganizationTick>
                <Checkmark width={12} height={12} style={{ stroke: RADOM_COLORS.BLACK }} />
              </OrganizationTick>
            : null}
        </OrganizationRowTrailing>
      </OrganizationRowButton>
    </OrganizationRowShell>
  }

  const renderOrganizationTree = (organization: IOrganization, depth = 0): React.ReactElement => {
    const childOrganizations = orderOrganizationsForDisplay(
      childOrganizationsByParent[organization.organizationId] ?? []
    )
    const isSelected = organization.organizationId === User.selectedOrganizationId

    return <OrganizationTreeGroup key={organization.organizationId}>
      {renderOrganizationRow(organization, {
        context: 'modal',
        selected: isSelected,
        nested: depth > 0,
        allowCopy: true,
        disabled: isChangingOrganization || organization.organizationId === 'qa-preview-org-secondary',
        onClick: isSelected
          ? () => setOrganizationModalVisible(false)
          : () => {
            void switchOrganization(organization)
          }
      })}
      {childOrganizations.length > 0 && (
        <OrganizationChildren>
          {childOrganizations.map(childOrganization => renderOrganizationTree(childOrganization, depth + 1))}
        </OrganizationChildren>
      )}
    </OrganizationTreeGroup>
  }

  const onClickNotifications = async (): Promise<void> => {
    Radom.updateLastBlockAck([
      ...Object.entries(EventsState.latestBlockScanned).map(([chainID, latestBlock]) =>
        ({ chainId: Number(chainID), lastBlockAck: latestBlock })
      ),
      { managedLastAck: new Date().toISOString() }
    ]).then(() => {
      setNumNotifications(0)
    })
  }

  const loadUserNotifications = async (): Promise<void> => {
    setIsLoadingAckedNotifications(true)
    Radom.getLastBlockAck()
      .then(acks => {
        const latestAcks = {}
        for (const ack of acks) {
          if (ack.chainId && ack.lastBlockAck) {
            latestAcks[ack.chainId] = ack.lastBlockAck
          } else if (ack.managedLastAck) {
            setLatestManagedAck(new Date(ack.managedLastAck))
          }
        }
        setLatestBlockAcks(latestAcks)
      })
      .catch(() => { })
      .finally(() => {
        setIsLoadingAckedNotifications(false)
      })
  }

  useEffect(() => {
    if (User.organization && User.organization.verificationStatus !== 'Verified') {
      setTestMode(true)
    }
  }, [User.organization])

  useEffect(() => {
    if (isLoadingAckedNotifications) return
    setNumNotifications(EventsState.managedEvents.filter(e => e.date.getTime() > latestManagedAck.getTime()).length)
  }, [EventsState.allEvents, latestBlockAcks])

  useEffect(() => {
    if (!Radom.isLoggedIn) {
      return
    }

    EventsState.loadEvents(testMode)
    Treasury.loadBalances()
    loadOrganizations()
    loadUserNotifications()
  }, [Radom.isLoggedIn, User.selectedOrganizationId])

  useEffect(() => {
    const handleResize = (): void => {
      setIsMobile(window.innerWidth <= 900)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!isMobile) {
      setIsMobileNavOpen(false)
    }
  }, [isMobile])

  const navIsHidden = NavbarController.isFullscreen || (isMobile && !isMobileNavOpen)
  const contentMarginLeft = NavbarController.isFullscreen || isMobile ? 0 : 228
  const topNavHeight = isMobile ? 108 : 66
  const toggleNavGroup = (groupName: string): void => {
    setActiveNavGroup(currentGroup => currentGroup === groupName ? undefined : groupName)
  }

  return (
    <div style={{
      margin: 'auto'
    }}>
      <GlobalStyles
        isModalOpen={NavbarController.isModalOpen ||
          (isMobile && isMobileNavOpen) ||
          isChangingOrganization} />
      <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
        <WithAuthentication>
          <div style={{
            display: 'flex',
            flexDirection: 'row'
          }}>

            {isMobile && isMobileNavOpen && <MobileOverlay onClick={() => setIsMobileNavOpen(false)} />}

            <RadomNavContainer $isHidden={navIsHidden} $isMobile={isMobile}>
              <RadomNav>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  width: '100%',
                  height: '100%',
                  minHeight: 0
                }}>

                  <div style={{
                    padding: '12px 18px 10px',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 10
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <NavRadomLogo to="/" onClick={() => {
                        setActiveNavGroup('Home')
                        if (isMobile) {
                          setIsMobileNavOpen(false)
                        }
                      }}>
                        <RadomLogo style={{ width: 70 }} />
                      </NavRadomLogo>

                      {isMobile &&
                        <IconButton
                          aria-label='Close navigation'
                          onClick={() => setIsMobileNavOpen(false)}
                          style={{ padding: 10 }}>
                          <Close width={18} height={18} />
                        </IconButton>
                      }
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '12px 10px 12px',
                    width: '100%',
                    gap: 5,
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden'
                  }}>
                    {routes
                      .filter(r => {
                        if ('testModeDisabled' in r && r.testModeDisabled && testMode) {
                          return false
                        }

                        if (!r.features) {
                          return true
                        }

                        for (const feature of r.features) {
                          if (!selectedOrg?.features?.[feature]) {
                            return false
                          }
                        }

                        return true
                      })
                      .filter(r => r.name).map(route => {
                        if (route.isBottomNav) return null
                        if (route.children) {
                          return <NavItemGroup
                            key={route.name}
                            $$active={activeNavGroup === route.name}>
                            <NavItemGroupText
                              $active={activeNavGroup === route.name}
                              role='button'
                              tabIndex={0}
                              onClick={() => toggleNavGroup(route.name ?? '')}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  toggleNavGroup(route.name ?? '')
                                }
                              }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {route.icon && <route.icon style={{ height: 16 }} />}
                                <span>{route.name}</span>
                              </div>
                              <Chevron id='chevron' fill='currentColor' />
                            </NavItemGroupText>

                            {

                              <NavItemGroupContainer $active={activeNavGroup === route.name}>

                                {
                                  route.children.map(route =>
                                    <NavItem
                                      style={{
                                        width: '100%'
                                      }}
                                      onClick={e => {
                                        e.stopPropagation()
                                        if (isMobile) {
                                          setIsMobileNavOpen(false)
                                        }
                                      }}
                                      key={route.name}
                                      $$active={location.pathname === route.path || (route.path === '/' && !location.pathname)}
                                      to={route.path ?? ''}>
                                      <NavItemText
                                        style={{ paddingLeft: 26, fontSize: 13.25 }}
                                        $active={location.pathname === route.path || (route.path === '/' && !location.pathname)}>{route.name}</NavItemText>
                                    </NavItem>
                                  )
                                }
                              </NavItemGroupContainer>
                            }
                          </NavItemGroup>
                        }

                        return <NavItem
                          key={route.name}
                          $$active={activeNavGroup === route.name}
                          onClick={() => {
                            setActiveNavGroup(route.name)
                            if (isMobile) {
                              setIsMobileNavOpen(false)
                            }
                          }}
                          to={route.path ?? ''}>
                          <NavItemText $active={activeNavGroup === route.name}>
                            {route.icon && <route.icon style={{ height: 15 }} />}
                            <span>{route.name}</span>
                          </NavItemText>
                        </NavItem>
                      })}
                  </div>
                </div>

              </RadomNav>
              <RadomBottomNav>
                {
                  routes.filter(r => r.isBottomNav).map(route => {
                    if (route.isExternal) {
                      return <a
                        key={route.name}
                        href={route.path}
                        target='_blank'
                        rel='noopener noreferrer'
                        style={{ textDecoration: 'none', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                        onClick={() => {
                          if (isMobile) setIsMobileNavOpen(false)
                        }}
                      >
                        <NavItemText $active={false}>
                          {route.icon && <route.icon style={{ width: 15 }} />}
                          <span>{route.name}</span>
                        </NavItemText>
                      </a>
                    }

                    return <NavItem
                      key={route.name}
                      $$active={activeNavGroup === route.name}
                      onClick={() => {
                        setActiveNavGroup(route.name)
                        if (isMobile) {
                          setIsMobileNavOpen(false)
                        }
                      }}
                      to={route.path ?? ''}
                    >
                      <NavItemText $active={activeNavGroup === route.name}>
                        {route.icon && <route.icon style={{ width: 15 }} />}
                        <span>{route.name}</span>
                      </NavItemText>
                    </NavItem>
                  })
                }
              </RadomBottomNav>
            </RadomNavContainer>

            <div style={{
              marginLeft: contentMarginLeft,
              transition: '0.2s ease all',
              width: `calc(100% - ${contentMarginLeft}px)`,
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'hidden'
            }}>
              <div style={{ width: '100%' }}>

                {!NavbarController.isFullscreen && <>
                  <div style={{ height: topNavHeight, width: '100%' }} />
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 4,
                    pointerEvents: 'none'
                  }}>
                    <div style={{
                      width: '100%',
                      margin: '0 auto'
                    }}>
                      <div style={{
                        marginLeft: contentMarginLeft,
                        display: 'flex',
                        alignItems: isMobile ? 'normal' : 'center',
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: 14,
                        background: '#ffffff',
                        borderBottom: '1px solid #edf1f7',
                        padding: '10px 18px',
                        pointerEvents: 'auto'
                      }}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}
                        >

                          {isMobile &&
                          <IconButton
                            aria-label='Open navigation'
                            onClick={() => setIsMobileNavOpen(true)}
                            style={{ padding: 10, display: 'flex', alignItems: 'center' }}>
                            <Menu width={18} height={18} />
                          </IconButton>
                          }

                          <SearchBox />
                        </div>

                        <div style={{ display: 'flex', height: '100%', alignItems: 'center', gap: 6, marginLeft: isMobile ? 0 : 'auto', alignSelf: isMobile ? 'flex-end' : 'normal' }}>
                          <HelpDropdown notificationsCloseFn={() => {}}
                            onClickHelpIcon={() => {}}
                            setNotificationsCloseFn={() => {}}
                          />
                          <NotificationDropdown onClickNotifications={onClickNotifications}
                            latestBlockAcks={latestBlockAcks}
                            latestManagedAck={latestManagedAck}
                            setLatestBlockAcks={setLatestBlockAcks}
                            setLatestManagedAck={setLatestManagedAck}
                            notificationsCloseFn={notificationsCloseFn}
                            numNotifications={numNotifications}
                            setNotificationsCloseFn={setNotificationsCloseFn}
                          />

                          <div style={{
                            width: 1,
                            height: 22,
                            background: '#e3e8f1',
                            margin: '0 6px 0 8px'
                          }} />

                          <Dropdown
                            onClick={() => {
                              loadOrganizations()
                            }}
                            overflow='hidden'
                            disabled={isChangingOrganization}
                            preferredAlignment='right'
                            onCloseFn={fn => setOrgDropdownCloseFn(() => fn)}
                            containerStyle={{ minWidth: 220, maxWidth: 'min(240px, 100%)' }}
                            outerStyle={{
                              background: isChangingOrganization ? '#f7f7f8' : '#ffffff',
                              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                              padding: '7px 11px 7px 12px',
                              borderRadius: 14,
                              gap: 10,
                              minHeight: 44,
                              marginLeft: 8
                            }}
                            innerStyle={{
                              width: 'min(360px, calc(100vw - 24px))',
                              maxHeight: 'min(calc(100vh - 88px), 620px)',
                              borderRadius: 16,
                              border: '1px solid #e7eaf0',
                              boxShadow: '0 20px 40px rgba(15, 23, 42, 0.12)'
                            }}
                            selectedContent={
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 2,
                                minWidth: 0
                              }}>
                                <span style={{
                                  fontSize: 10.5,
                                  fontWeight: 600,
                                  letterSpacing: '0.05em',
                                  textTransform: 'uppercase',
                                  color: 'rgba(17, 17, 17, 0.45)'
                                }}>
                            Organization
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                                  {isChangingOrganization
                                    ? <Spinner style={{ width: 14, height: 14 }} />
                                    : null}
                                  <span style={{
                                    maxWidth: 138,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: RADOM_COLORS.BLACK,
                                    fontSize: 13,
                                    fontWeight: 500
                                  }}>
                                    {isChangingOrganization && organizationTransitionLabel
                                      ? organizationTransitionLabel
                                      : organizationDisplayName(selectedOrg)}
                                  </span>
                                  <ModePill $test={testMode}>{testMode ? 'Test' : 'Live'}</ModePill>
                                </div>
                              </div>
                            }
                            dropdownContent={
                              <OrganizationDropdownContent>
                                {
                                  User._user && <>
                                    <OrganizationDropdownBody>
                                      {currentVisibleOrganization && <>
                                        <div style={{ padding: '0 12px' }}>
                                          <OrganizationSectionLabel>Current</OrganizationSectionLabel>
                                        </div>
                                        <div style={{ padding: '0 8px' }}>
                                          {renderOrganizationRow(currentVisibleOrganization, {
                                            context: 'dropdown',
                                            selected: true,
                                            showId: true,
                                            allowCopy: true,
                                            disabled: isChangingOrganization
                                          })}
                                        </div>
                                      </>}

                                      {otherVisibleOrganizations.length > 0 && <>
                                        <OrganizationSectionDivider />
                                        <div style={{ padding: '0 12px' }}>
                                          <OrganizationSectionLabel>Related organizations</OrganizationSectionLabel>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 8px' }}>
                                          {otherVisibleOrganizations.map(o =>
                                            <div key={o.organizationId}>
                                              {renderOrganizationRow(o, {
                                                context: 'dropdown',
                                                disabled: isChangingOrganization || o.organizationId === 'qa-preview-org-secondary',
                                                onClick: () => {
                                                  void switchOrganization(o)
                                                }
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </>}

                                      <OrganizationDropdownActions>
                                        <DropdownItem
                                          onClick={() => {
                                            orgDropdownCloseFn()
                                            window.setTimeout(() => setOrganizationModalVisible(true), 0)
                                          }}
                                          style={{
                                            width: '100%',
                                            padding: '11px 12px',
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 10,
                                            color: RADOM_COLORS.GRAY_DARKEST,
                                            cursor: 'pointer',
                                            borderRadius: 12
                                          }}>
                                          <span style={{
                                            fontSize: 13,
                                            lineHeight: 1,
                                            opacity: 0.6,
                                            letterSpacing: 0.6,
                                            paddingTop: 3
                                          }}>•••</span>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <OrganizationDropdownActionLabel>
                                              View all organizations
                                            </OrganizationDropdownActionLabel>
                                            <OrganizationDropdownActionHint>
                                              {hiddenOrganizationsCount > 0
                                                ? `Browse the full hierarchy and ${hiddenOrganizationsCount} more ` +
                                                  `organization${hiddenOrganizationsCount === 1 ? '' : 's'}.`
                                                : 'Browse the full hierarchy and switch merchant context.'}
                                            </OrganizationDropdownActionHint>
                                          </div>
                                        </DropdownItem>

                                        <DropdownItem
                                          onClick={() => {
                                            orgDropdownCloseFn()
                                            window.setTimeout(() => setCreateOrganizationModalVisible(true), 0)
                                          }}
                                          style={{
                                            width: '100%',
                                            gap: 10,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            padding: '11px 12px',
                                            color: RADOM_COLORS.BLACK,
                                            borderRadius: 12
                                          }}>
                                          <span style={{
                                            fontSize: 13,
                                            lineHeight: 1,
                                            color: RADOM_COLORS.GRAY_DARKER,
                                            paddingTop: 3
                                          }}>+</span>
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <OrganizationDropdownActionLabel>
                                              Create new organization
                                            </OrganizationDropdownActionLabel>
                                            <OrganizationDropdownActionHint>
                                              Start a new organization workspace and switch into it.
                                            </OrganizationDropdownActionHint>
                                          </div>
                                        </DropdownItem>
                                      </OrganizationDropdownActions>
                                    </OrganizationDropdownBody>

                                    <OrganizationDropdownFooter>
                                      <div style={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 10,
                                        color: RADOM_COLORS.BLACK,
                                        padding: '2px 6px 0'
                                      }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ fontSize: 13, fontWeight: 400 }}>Mode</span>
                                            <Tooltip message={
                                              canUseLiveMode
                                                ? 'Switch between test and live data for the dashboard.'
                                                : 'To enable live mode, please verify your organization.'
                                            }>
                                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <HoverInfoIcon width={15} height={15} />
                                              </div>
                                            </Tooltip>
                                          </div>
                                          <span style={{ fontSize: 11.5, color: '#8a91a0', lineHeight: 1.35 }}>
                                            {testMode ? 'Sandbox balances, payouts and accounts' : 'Production balances, payouts and accounts'}
                                          </span>
                                        </div>
                                        <ModeToggle>
                                          <ModeToggleButton
                                            type='button'
                                            $active={!testMode}
                                            disabled={!canUseLiveMode}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              if (canUseLiveMode) {
                                                setTestMode(false)
                                              }
                                            }}>
                                      Live
                                          </ModeToggleButton>
                                          <ModeToggleButton
                                            type='button'
                                            $active={testMode}
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              if (!testMode) {
                                                setTestMode(true)
                                              }
                                            }}>
                                      Test
                                          </ModeToggleButton>
                                        </ModeToggle>
                                      </div>

                                      {canShowImpersonationControls && <DropdownItem
                                        onClick={handleImpersonationAction}
                                        style={{
                                          width: '100%',
                                          margin: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: RADOM_COLORS.BLACK,
                                          padding: '10px 12px',
                                          borderRadius: 12,
                                          border: '1px solid #e7eaf0',
                                          background: '#ffffff'
                                        }}>
                                        <span style={{ fontSize: 13.75, fontWeight: 520 }}>
                                          {isImpersonatingOrganization ? 'Stop impersonating' : 'Impersonate Org'}
                                        </span>
                                      </DropdownItem>}

                                      <DropdownItem
                                        onClick={() => {
                                          orgDropdownCloseFn()
                                          window.setTimeout(() => {
                                            Radom.signOut()
                                          }, 0)
                                        }}
                                        style={{
                                          width: '100%',
                                          margin: 0,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: RADOM_COLORS.BLACK,
                                          padding: '10px 12px',
                                          borderRadius: 12,
                                          border: '1px solid #e7eaf0',
                                          background: '#f7f8fa'
                                        }}>
                                        <span style={{ fontSize: 13.75, fontWeight: 520 }}>Sign out</span>
                                      </DropdownItem>
                                    </OrganizationDropdownFooter>
                                  </>
                                }
                              </OrganizationDropdownContent>
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>}

                <Routes>
                  {
                    routes.map((route, index) => <Route
                      key={index}
                      path={route.path}
                      element={route.element}
                    />
                    )
                  }
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <InviteAcceptModal />
                {
                  isChangingOrganization &&
                  <AppBlocker>
                    <AppBlockerCard>
                      <Spinner style={{ width: 24, height: 24 }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <span style={{ fontSize: 15, fontWeight: 600, color: RADOM_COLORS.BLACK }}>
                          Switching organization
                        </span>
                        <span style={{ fontSize: 13.5, color: RADOM_COLORS.GRAY_DARKEST, lineHeight: 1.45 }}>
                          {organizationTransitionLabel
                            ? `Loading ${organizationTransitionLabel}. This can take a moment.`
                            : 'Loading the selected organization. This can take a moment.'}
                        </span>
                      </div>
                    </AppBlockerCard>
                  </AppBlocker>
                }
              </div>
            </div>

          </div>

          <Modal
            visible={organizationModalVisible}
            onClose={() => setOrganizationModalVisible(false)}
            title='Select organization'
            innerStyles={{ width: 'min(680px, calc(100vw - 40px))' }}>

            <div style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              fontSize: 14,
              gap: 10
            }}>
              {isChangingOrganization && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.6)',
                    zIndex: 10
                  }}
                >
                  <Spinner />
                </div>
              )}
              <div style={{ color: RADOM_COLORS.GRAY_DARKEST, fontSize: 13.25, lineHeight: 1.5, maxWidth: 540 }}>
                Switch dashboard organization.
              </div>

              {currentModalOrganization && <div>
                <OrganizationSectionLabel>Current</OrganizationSectionLabel>
                <OrganizationModalList>
                  {renderOrganizationRow(currentModalOrganization, {
                    context: 'modal',
                    selected: true,
                    allowCopy: true,
                    disabled: isChangingOrganization,
                    onClick: () => setOrganizationModalVisible(false)
                  })}
                </OrganizationModalList>
              </div>}

              {organizationHierarchyRoots.length > 0 && <div>
                <OrganizationSectionLabel>Organization hierarchy</OrganizationSectionLabel>
                <OrganizationModalList>
                  {organizationHierarchyRoots.map(organization => renderOrganizationTree(organization))}
                </OrganizationModalList>
              </div>}
            </div>

          </Modal>

          <Modal
            visible={createOrganizationModalVisible}
            onClose={() => setCreateOrganizationModalVisible(false)}
            title='Create organization'
            innerStyles={{ width: '420px', minHeight: 'auto' }}>
            <span style={{ fontSize: 14, color: RADOM_COLORS.GRAY_DARKEST, lineHeight: 1.6 }}>
              Create a new organization and switch into it right away.
            </span>
            <ModalFooter $fullWidthAction>
              <SecondaryButton
                type='button'
                disabled={isChangingOrganization}
                onClick={() => setCreateOrganizationModalVisible(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type='button'
                disabled={isChangingOrganization}
                onClick={createOrganization}>
                Create organization
              </PrimaryButton>
            </ModalFooter>
          </Modal>

          <Modal
            title='Impersonate organization'
            visible={impersonationModalOpen && isActiveImpersonationSourceOrg}
            onClose={() => setImpersonationModalOpen(false)}>
            <div style={{ display: 'flex', padding: '0 30px 30px 30px', width: '100%' }}>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: '100%' }}>
                <InputLabel style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>
                  Organization ID
                </InputLabel>
                <TextInput
                  placeholder='00000000-0000-0000-0000-000000000000'
                  value={impersonationOrgId}
                  onChange={e => {
                    setImpersonationOrgId(e.target.value)
                  }} />
              </div>
            </div>

            <div style={{ padding: '0 30px 30px 30px' }}>
              <PrimaryButton
                style={{ width: '100%' }}
                disabled={!impersonationOrgId || !isActiveImpersonationSourceOrg}
                onClick={() => {
                  if (!isActiveImpersonationSourceOrg) {
                    return
                  }

                  localStorage.setItem(IMPERSONATION_ORG_ID_KEY, impersonationOrgId)
                  window.location.reload()
                }}>
                Impersonate
              </PrimaryButton>
            </div>
          </Modal>
        </WithAuthentication>
        <Toaster position="bottom-right" />
      </GoogleOAuthProvider>
    </div>
  )
})

export default App
